import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getNovelById, getChaptersByNovel } from '../services/api';
import { ArrowLeft, BookOpen } from 'lucide-react';

const NovelDetail = () => {
  const { id } = useParams();
  const [novel, setNovel] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNovelData();
  }, [id]);

  const fetchNovelData = async () => {
    try {
      const [novelData, chaptersData] = await Promise.all([
        getNovelById(id),
        getChaptersByNovel(id)
      ]);
      setNovel(novelData);
      setChapters(chaptersData);
    } catch (error) {
      console.error('Error fetching novel:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><p className="text-gray-600">Loading...</p></div>;
  }

  if (!novel) {
    return <div className="flex items-center justify-center min-h-screen"><p className="text-gray-600">Novel not found</p></div>;
  }

  return (
    <div className="py-16 px-6">
      <div className="max-w-4xl mx-auto">
        <Link to="/novels" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-8">
          <ArrowLeft size={16} />Back to Novels
        </Link>

        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4">{novel.title}</h1>
          <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
            <span className={`px-3 py-1 uppercase tracking-wider ${novel.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
              {novel.status}
            </span>
            <span>{novel.totalChapters} {novel.totalChapters === 1 ? 'chapter' : 'chapters'}</span>
          </div>
          <p className="text-lg text-gray-700 leading-relaxed">{novel.description}</p>
        </div>

        <div className="bg-gray-50 border border-gray-200 p-8">
          <div className="flex items-center gap-3 mb-6">
            <BookOpen size={24} className="text-gray-900" />
            <h2 className="text-2xl font-serif text-gray-900">Chapters</h2>
          </div>
          <div className="space-y-4">
            {chapters.map((chapter) => (
              <Link key={chapter.id} to={`/novel/${id}/chapter/${chapter.id}`} className="block border border-gray-200 p-6 bg-white hover:border-gray-400 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-sm uppercase tracking-wider text-gray-500">Chapter {chapter.chapterNumber}</span>
                      <h3 className="text-xl font-serif text-gray-900">{chapter.title}</h3>
                    </div>
                    <p className="text-sm text-gray-500">{new Date(chapter.publishedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                  <span className="text-gray-400 group-hover:text-gray-900">→</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NovelDetail;
